// Phase 88: unified custom multi-buy discount preview.
//
// Replaces the per-flow petDiscount.ts + kidDiscount.ts helpers. Pet, kid
// and make-your-own are now ONE discount (the "RK Custom Multi-Buy" smart
// collection + the rk-pet-tiered-discount Function), unified 2026-06-11.
//
// Mirrors the live Shopify Function tier:
//   - 1st item: full price
//   - 2nd item: 15% off
//   - 3rd+ item: 20% off
// The full-price unit is the most expensive qualifying unit (margin-safe),
// matching the Function's "discount the cheaper line" rule.
//
// Qualifying = any customiser-enabled product:
//   - The PDP's primary product ALWAYS qualifies. The customiser only loads
//     on products carrying a live template tag, which is exactly the smart
//     collection's membership rule, so the primary is always in the
//     collection by construction.
//   - A bundle member qualifies when it matched a live customiser template,
//     which the config resolver signals with a non-null `flowKind`. Same
//     collection-membership rule, so the preview matches checkout.
//
// Pure, no React, no side effects. Safe to import from anywhere.

import type { BundleMember } from "../types";

/** A bundle member counts toward the multi-buy when it matched a live
 *  customiser template - the config resolver gives those a non-null
 *  `flowKind`, which is exactly when the product sits in the discount's
 *  smart collection. Null/empty = not a customiser product = no discount. */
export function memberQualifies(flowKind: string | null | undefined): boolean {
  return flowKind != null && flowKind !== "";
}

/** Tier discount percentage for the Nth qualifying item the customer owns.
 *  position is 1-indexed (1st = 0%, 2nd = 15%, 3rd+ = 20%). */
export function customDiscountFor(position: number): number {
  if (position <= 1) return 0;
  if (position === 2) return 15;
  return 20;
}

/** Compute the per-member discount percentage + total savings preview for
 *  the bundle "Add to your set" section.
 *
 *  Inputs:
 *  - `primaryQualifies`: whether the PDP product counts (always true on a
 *    customiser PDP; passed in so the caller stays the source of truth).
 *  - `primaryPriceCents`: the PDP product's variant price. Used to apply
 *    the primary's own tier AND to rank by price within the bundle.
 *  - `members`: every bundle member with the customer's current selection
 *    (`included = true` = the line will be added) and its `flowKind`.
 *  - `existingUnitPriceCents`: the per-unit GARMENT prices of qualifying
 *    customiser units already in /cart.js (qty-weighted), so the preview
 *    ranks existing + new units globally by price - exactly like the
 *    checkout Function - instead of assuming existing units occupy the
 *    top tier slots regardless of price (which over-promised savings
 *    whenever the cart held a CHEAPER custom item than the new one).
 *
 *  Returns:
 *  - `primaryDiscountPct`: 0-20, baked into "Item total" in the foot.
 *  - `memberDiscountPct`: { [memberId]: pct } for per-row strike-through.
 *  - `totalSavingsCents`: the INCREMENTAL cart savings caused by adding
 *    the new lines (function total after minus before), for the
 *    "You're saving $X" strip. Never negative. */
export function assignCustomDiscounts(input: {
  primaryQualifies: boolean;
  primaryPriceCents: number | null;
  members: Array<{
    id: string;
    flowKind: string | null;
    included: boolean;
    priceCents: number | null;
    quantity: number;
  }>;
  existingUnitPriceCents: number[];
}): {
  primaryDiscountPct: number;
  memberDiscountPct: Record<string, number>;
  totalSavingsCents: number;
} {
  const out = {
    primaryDiscountPct: 0,
    memberDiscountPct: {} as Record<string, number>,
    totalSavingsCents: 0,
  };

  type Unit = {
    kind: "existing" | "primary" | "member";
    memberId?: string;
    priceCents: number;
  };
  const units: Unit[] = [];

  for (const p of input.existingUnitPriceCents) {
    if (typeof p === "number" && p > 0) {
      units.push({ kind: "existing", priceCents: p });
    }
  }
  if (input.primaryQualifies && typeof input.primaryPriceCents === "number") {
    units.push({ kind: "primary", priceCents: input.primaryPriceCents });
  }
  for (const m of input.members) {
    if (!m.included) continue;
    if (!memberQualifies(m.flowKind)) continue;
    if (typeof m.priceCents !== "number") continue;
    for (let q = 0; q < (m.quantity ?? 1); q++) {
      units.push({ kind: "member", memberId: m.id, priceCents: m.priceCents });
    }
  }

  if (units.length === 0) return out;

  // Tier savings over a price list, dearest first: unit 1 full price,
  // unit 2 -15%, unit 3+ -20%, rounding per unit in integer cents.
  // Mirrors extensions/pet-tiered-discount/src/run.ts exactly.
  const tierSavings = (prices: number[]): number => {
    const sorted = [...prices].sort((a, b) => b - a);
    let total = 0;
    for (let i = 0; i < sorted.length; i++) {
      const pct = customDiscountFor(i + 1);
      if (pct > 0) total += Math.round((sorted[i] * pct) / 100);
    }
    return total;
  };

  // Global price ranking across existing + new units, matching the
  // Function's cart-wide sort - the dearest unit keeps full price no
  // matter which line it sits on.
  const sortedUnits = [...units].sort((a, b) => b.priceCents - a.priceCents);
  for (let idx = 0; idx < sortedUnits.length; idx++) {
    const pct = customDiscountFor(idx + 1);
    if (pct <= 0) continue;
    const u = sortedUnits[idx];
    if (u.kind === "primary") {
      out.primaryDiscountPct = Math.max(out.primaryDiscountPct, pct);
    } else if (u.kind === "member" && u.memberId) {
      out.memberDiscountPct[u.memberId] = Math.max(
        out.memberDiscountPct[u.memberId] ?? 0,
        pct,
      );
    }
  }

  // "You're saving $X" = the INCREMENTAL saving the new lines cause,
  // wherever the Function ends up allocating it (it may discount an
  // existing cheaper unit rather than the new one).
  const before = tierSavings(input.existingUnitPriceCents);
  const after = tierSavings(units.map((u) => u.priceCents));
  out.totalSavingsCents = Math.max(0, after - before);

  return out;
}

// Re-export for App.tsx's type import convenience.
export type { BundleMember };
